<template>
  <div class="whatsapp-qr-section">
    <!-- Connected State -->
    <div v-if="status === 'open'" class="connection-status connected">
      <div class="status-icon">
        <fluent-icon icon="checkmark-circle" size="48" />
      </div>
      <h3 class="status-title">{{ $t('INBOX_MGMT.WHATSAPP_QR.CONNECTED_TITLE') }}</h3>
      <p class="status-description">
        {{ $t('INBOX_MGMT.WHATSAPP_QR.CONNECTED_DESC', { phone: phoneNumber }) }}
      </p>
      <woot-button variant="smooth" color-scheme="alert" style="cursor:pointer" @click="confirmDisconnect">
        {{ $t('INBOX_MGMT.WHATSAPP_QR.DISCONNECT_BTN') }}
      </woot-button>
    </div>

    <!-- Disconnected / QR State -->
    <div v-else-if="status !== 'open' && status !== 'loading'" class="connection-status disconnected">
      <h3 class="status-title">{{ $t('INBOX_MGMT.WHATSAPP_QR.SCAN_TITLE') }}</h3>
      <p class="status-description">{{ $t('INBOX_MGMT.WHATSAPP_QR.SCAN_DESC') }}</p>
      <div v-if="qrBase64" class="qr-wrapper">
        <img :src="'data:image/png;base64,' + qrBase64" alt="QR Code WhatsApp" class="qr-image" />
        <p class="qr-countdown">
          {{ $t('INBOX_MGMT.WHATSAPP_QR.EXPIRES_IN', { seconds: countdown }) }}
        </p>
        <woot-button variant="smooth" @click="fetchQr">
          {{ $t('INBOX_MGMT.WHATSAPP_QR.REFRESH_BTN') }}
        </woot-button>
      </div>
      <div v-else class="qr-loading">
        <span class="spinner" />
        <p>{{ $t('INBOX_MGMT.WHATSAPP_QR.LOADING_QR') }}</p>
      </div>
    </div>

    <!-- Connecting / loading State -->
    <div v-else class="connection-status loading">
      <span class="spinner" />
      <p>{{ $t('INBOX_MGMT.WHATSAPP_QR.CHECKING') }}</p>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'WhatsAppQrCode',
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      status: 'loading',
      qrBase64: null,
      phoneNumber: '',
      pollingInterval: null,
      countdownInterval: null,
      countdown: 60,
      resolvedInstanceName: '',
    };
  },
  computed: {
    evolutionApiUrl() {
      return 'https://evo.bruzap.com.br';
    },
    evolutionApiKey() {
      return import.meta.env.VITE_EVOLUTION_API_KEY || '';
    },
    instanceName() {
      if (this.resolvedInstanceName) return this.resolvedInstanceName;
      const webhook = this.inbox && this.inbox.webhook_url ? this.inbox.webhook_url : '';
      const match = webhook.match(/\/webhook\/([^/]+)$/);
      return match ? match[1] : '';
    },
  },
  mounted() {
    this.checkStatus();
    this.startPolling();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    findInstance(instances) {
      // Strategy 1: match by webhook_url instance name
      if (this.instanceName) {
        const byName = instances.find(
          i => i.name === this.instanceName
        );
        if (byName) return byName;
      }

      // Strategy 2: match by Chatwoot.nameInbox (exact inbox name match)
      // Prefer open > connecting > others
      if (this.inbox && this.inbox.name) {
        const byInboxName = instances.filter(
          i => i.Chatwoot && i.Chatwoot.nameInbox === this.inbox.name
        );
        if (byInboxName.length > 0) {
          return (
            byInboxName.find(i => i.connectionStatus === 'open') ||
            byInboxName[byInboxName.length - 1]
          );
        }
      }

      // Strategy 3: slug prefix match, prefer open
      if (this.inbox && this.inbox.name) {
        const slug = this.inbox.name
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/, '');
        const bySlug = instances.filter(
          i => i.name === slug || i.name.startsWith(slug + '-')
        );
        if (bySlug.length > 0) {
          return (
            bySlug.find(i => i.connectionStatus === 'open') ||
            bySlug[bySlug.length - 1]
          );
        }
      }

      return null;
    },

    async checkStatus() {
      try {
const resp = await axios.get(
          this.evolutionApiUrl + '/instance/fetchInstances',
          { headers: { apikey: this.evolutionApiKey } }
        );
        const instances = Array.isArray(resp.data) ? resp.data : [];
        const inst = this.findInstance(instances);

        if (inst) {
          if (inst.name && !this.resolvedInstanceName) {
            this.resolvedInstanceName = inst.name;
          }
          const state = inst.connectionStatus;
          this.status = state || 'disconnected';
          if (state === 'open') {
            const owner = inst.ownerJid || '';
            this.phoneNumber = owner
              .replace('@s.whatsapp.net', '')
              .replace(/^55/, '+55 ');
          } else {
            await this.fetchQr();
          }
        } else {
          this.status = 'disconnected';
        }
      } catch (e) {
        console.error('[WhatsAppQrCode] checkStatus error:', e);
        this.status = 'disconnected';
      }
    },

    async fetchQr() {
      if (!this.instanceName) return;
      try {
        const resp = await axios.get(
          this.evolutionApiUrl + '/instance/connect/' + this.instanceName,
          { headers: { apikey: this.evolutionApiKey } }
        );
        const data = resp.data;
        let b64 = data.base64 || (data.qrcode && data.qrcode.base64) || data.qr || null;
        if (b64 && b64.startsWith('data:')) {
          b64 = b64.split(',')[1];
        }
        this.qrBase64 = b64;
        if (b64) this.resetCountdown();
      } catch (e) {
        console.error('[WhatsAppQrCode] fetchQr error:', e);
        this.qrBase64 = null;
      }
    },

    startPolling() {
      this.pollingInterval = setInterval(() => {
        this.checkStatus();
      }, 20000);
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    },

    resetCountdown() {
      if (this.countdownInterval) clearInterval(this.countdownInterval);
      this.countdown = 60;
      this.countdownInterval = setInterval(() => {
        this.countdown -= 1;
        if (this.countdown <= 0) {
          clearInterval(this.countdownInterval);
          this.fetchQr();
        }
      }, 1000);
    },

    confirmDisconnect() {
      if (window.confirm(this.$t('INBOX_MGMT.WHATSAPP_QR.DISCONNECT_CONFIRM'))) {
        this.disconnect();
      }
    },
    async disconnect() {
      try {
        await axios.delete(
          this.evolutionApiUrl + '/instance/logout/' + this.instanceName,
          { headers: { apikey: this.evolutionApiKey } }
        );
        this.status = 'disconnected';
        this.phoneNumber = '';
        this.resolvedInstanceName = '';
        await this.fetchQr();
      } catch (e) {
        console.error('[WhatsAppQrCode] disconnect error:', e);
      }
    },
  },
};
</script>

<style scoped>
.whatsapp-qr-section {
  padding: 2.4rem;
}
.connection-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.6rem;
  text-align: center;
  max-width: 36rem;
  margin: 0 auto;
}
.status-icon {
  color: #25d366;
}
.status-title {
  font-size: 1.8rem;
  font-weight: 600;
}
.status-description {
  font-size: 1.4rem;
  color: var(--color-body);
}
.qr-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.2rem;
}
.qr-image {
  width: 22rem;
  height: 22rem;
  border: 1px solid var(--color-border);
  border-radius: 0.8rem;
  padding: 0.8rem;
  background: #fff;
}
.qr-countdown {
  font-size: 1.2rem;
  color: var(--color-body);
}
.qr-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.2rem;
}
.spinner {
  display: inline-block;
  width: 3.2rem;
  height: 3.2rem;
  border: 3px solid var(--color-border);
  border-top-color: var(--w-color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
